import React, { useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { ContractValuesDetails, DebitFGTSInstallments, HeroDataInfo, ScreenResponsive } from "../../../components";
import {
	Box,
	ButtonLink,
	DataCard,
	Grid,
	GridItem,
	IconCheckedLight,
	skinVars,
	Text3,
	useScreenSize,
} from "@telefonica/mistica";
import { strings } from "../../../localization";
import { useGlobalStore } from "../../../contexts/globalStoreContext";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { TabSelectedEnum } from "../../../stores";
import { TimeLimitCacheQITechEnum, useNavigateCustom } from "../../../hooks";

interface Fields {
	IOF: number;
	anticipationValue: number;
	anticipationYears: number;
	blockedFGTSValue: number;
	contractFees: number;
	monthFees: number;
	yearsFees: number;
	tacAmount: number;
}

interface Installment {
	index: number;
	data: string;
	value: number;
}

const AnticipationFinished: React.FC = observer(() => {
	const navigation = useNavigateCustom();
	const { isDesktopOrBigger } = useScreenSize();
	const { updatePageView, addAnalyticEvent } = useAnalytics();

	const { anticipationStore: store, authenticationStore } = useGlobalStore();
	const tabSelected = store.getTabSelected();
	const isPartial = tabSelected === TabSelectedEnum.PARTIAL;
	const partialData = store.partialAnticipationShelf.partialSimulationData;
	const totalData = store.totalAnticipationShelf;

	const { toGoOut } = strings.components.buttons;
	const { title, description } = strings.pages.signContract.anticipationFinished;
	const { title: titleInstallments } = strings.components.debitFGTSInstallments;

	React.useEffect(() => {
		updatePageView({ name: "Vivo Money FGTS Antecipação Realizada", pagePath: "/fgts/antecipacao-realizada" });
	}, [isPartial]);

	const onHandleGoOut = (): void => {
		addAnalyticEvent({ action: "click.button", step: "fgts:antecipacao-realizada", description: toGoOut }, { applySanitize: true});
		authenticationStore.logout();
		navigation("/", { replace: true });
	};

	const renderFields = useCallback((): Fields => {
		if (isPartial) {
			return {
				IOF: partialData.value?.iofAmount || 0,
				anticipationValue: partialData.value?.disbursedIssueAmount || 0,
				anticipationYears: partialData.value?.yearsInAdvance || 0,
				blockedFGTSValue: partialData.value?.totalAmount || 0,
				contractFees: partialData.value?.totalPreFixedAmount || 0,
				monthFees: partialData.value?.monthlyRate || 0,
				yearsFees: partialData.value?.annualRate || 0,
				tacAmount: partialData.value?.tacAmount || 0,
			};
		}

		return {
			IOF: totalData.IOF.value,
			anticipationValue: totalData.disbursedIssueAmount.value,
			anticipationYears: totalData.yearsAdvance.value,
			blockedFGTSValue: totalData.blockedValue.value,
			contractFees: totalData.contractFees.value,
			monthFees: totalData.monthFees.value || 0,
			yearsFees: totalData.yearsFees.value || 0,
			tacAmount: totalData.tacAmount.value || 0,
		};
	}, [tabSelected]);

	const renderInstallments = useCallback((): Installment[] => {
		const installments: Installment[] = [];
		if (isPartial) {
			partialData.value?.installments.map((installment, index) =>
				installments.push({ index, data: installment.dueDate, value: installment.totalAmount }),
			);

			return installments;
		}

		totalData.installments.value.map((installment, index) =>
			installments.push({ index, data: installment.dueDate, value: installment.totalAmount }),
		);

		return installments;
	}, [tabSelected]);

	const handlePopState = (): void => {
		authenticationStore.logout();
		navigation("/", { replace: true });
	};

	useEffect(() => {
		// Limpa o sessionStorage com timer cache da QiTech
		sessionStorage.removeItem(TimeLimitCacheQITechEnum.START);

		// Adiciona um listener ao evento popstate
		window.addEventListener("popstate", handlePopState);

		// Limpa o listener ao desmontar o componente
		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [navigation]);

	return (
		<ScreenResponsive
			isFullHeight
			responsiveLayoutProps={{ backgroundColor: "#F5F5F5" }}
		>
			<Box
				paddingTop={isDesktopOrBigger ? 40 : 32}
				paddingBottom={isDesktopOrBigger ? 80 : 32}
				paddingX={isDesktopOrBigger ? 0 : 8}
			>
				<Grid gap={40} columns={{ maxSize: 600 }} justifyContent={"center"}>
					<GridItem alignSelf="center">
						<Box paddingBottom={32}>
							<HeroDataInfo
								icon={<IconCheckedLight size={64} color={skinVars.colors.success} />}
								title={title}
								description={description}
							/>
						</Box>
						<Grid columns={{ maxSize: 392 }} gap={32} justifyContent={"center"}>
							<DataCard
								extra={
									<Grid gap={32}>
										<ContractValuesDetails
											anticipationValue={renderFields().anticipationValue}
											anticipationYears={renderFields().anticipationYears}
											IOF={renderFields().IOF}
											blockedFGTSValue={renderFields().blockedFGTSValue}
											contractFees={renderFields().contractFees}
											monthFees={renderFields().monthFees}
											yearsFees={renderFields().yearsFees}
											tacAmount={renderFields().tacAmount}
										/>
										<DebitFGTSInstallments
											installments={renderInstallments()}
											analytic={async () => await addAnalyticEvent({ action: "click.button", step: "fgts:antecipacao-realizada", description: titleInstallments }, { applySanitize: true})}
										/>
									</Grid>
								}
							/>

							<ButtonLink onPress={onHandleGoOut}>
								<Text3 medium color={skinVars.colors.textLink} textAlign="center">
									{toGoOut}
								</Text3>
							</ButtonLink>

						</Grid>
					</GridItem>
				</Grid>
			</Box>
		</ScreenResponsive>
	);
});

export default AnticipationFinished;
